import styled, { createGlobalStyle } from 'styled-components';
import { useState } from 'react';
import { compose } from '../../utils/compose';
import { withStrictMode } from '../../utils/with-strict-mode';
import { device, shadows } from '../../../utils/styled-component-mixins';
import { useLocalization } from '../../custom-hooks/useLocalization';
import useInsights from '../../custom-hooks/use-insight-grid-filters';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import { withServerStyles } from '../../shared/ServerStylesConditionalWrapper';
import BdoSelectInputContainer from '../../shared/BdoSelectInput/BdoSelectInputContainer';
import EventWebinarCard from '../cards/EventWebinarCard/EventWebinarCard';
import InsightGrid from '../InsightGrid';
import Switch from '../Switch';

const ContainerStyles = createGlobalStyle`
  @media ${device.desktop} {
    .bdo-select-container .select-input-wrapper {
      padding-left: 0 !important;
    }
  }

  .hero {
    margin-bottom: 4rem
  }
`;

const StyledEventWebinarCard = styled(EventWebinarCard)`
  position: relative;

  & > div {
    flex: 1 1 100%;
  }

  &::after {
    ${shadows.cardShadow}

    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -1;
  }
`;

const StyledSwitch = styled(Switch)`
  position: relative;
`;

const Header = styled.div`
  @media ${device.deviceOnly} {
    display: flex;
    flex-direction: column;
    align-items: start;
    margin-bottom: 4rem;
  }
`;

/**
 * @param {import('./EventWebinarLandingInsightList').EventWebinarLandingInsightList} props
 * @returns {import('react').ReactElement}
 */
const EventWebinarLandingInsightList = ({
  className,
  industryCategories,
  serviceCategories,
  selectedIndustry,
  selectedService,
  selectedContentType,
  shouldOnlyIncludeCpe: preselectedShouldOnlyIncludeCpe,
  eventWebinars,
  totalResults,
  advancedSearchPrelinkText,
  advancedSearchLinkText,
  advancedSearchLink,
  notContentType
}) => {
  const { resourceStrings } = useLocalization();
  const filterLabel = resourceStrings['bdO.EventsWebinarsSliderWidget.FilterLabel'];
  const typeLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Type'];
  const eventWebinarTypes = {
    0: resourceStrings['bdO.EventsWebinars.Event'],
    1: resourceStrings['bdO.EventsWebinars.Webinar'],
    2: resourceStrings['bdO.EventsWebinars.PastWebinar']
  };

  const [shouldIncludeOnlyCpe, setShouldIncludeOnlyCpe] = useState(preselectedShouldOnlyIncludeCpe);
  const [selectedEventWebinarType, setSelectedEventWebinarType] = useState(selectedContentType);

  const { filters, results, isLoading, hasMoreResults, showMore } = useInsights({
    apiSlug: 'events',
    initialResults: eventWebinars,
    initialTotalCount: totalResults,
    industryCategories,
    serviceCategories,
    selectedIndustry,
    selectedService,
    shouldIncludeOnlyCpe,
    notContentType,
    isLanding: true,
    additionalCategories: [
      {
        categories: eventWebinarTypes,
        defaultSelectedCategory: selectedEventWebinarType,
        label: typeLabel,
        state: selectedEventWebinarType,
        setState: setSelectedEventWebinarType,
        queryStringKey: 'contentType'
      }
    ]
  });

  return (
    <div className={className}>
      <ContainerStyles />
      <InsightGrid
        header={
          <Header>
            <BdoSelectInputContainer
              className="bdo-select-container"
              filterLabel={filterLabel}
              filters={filters}
            />
            <StyledSwitch
              label="CPE Credits"
              state={shouldIncludeOnlyCpe}
              setState={setShouldIncludeOnlyCpe}
            />
          </Header>
        }
        card={StyledEventWebinarCard}
        cards={results}
        isLoading={isLoading}
        hasMoreResults={hasMoreResults}
        showMore={showMore}
        advancedSearchPrelinkText={advancedSearchPrelinkText}
        advancedSearchLinkText={advancedSearchLinkText}
        advancedSearchLink={advancedSearchLink}
        indexKeyFunction={(props) => props.nodeId}
      />
    </div>
  );
};

const StyledEventWebinarLandingInsightList = styled(EventWebinarLandingInsightList)`
  margin-bottom: 8rem;
  margin-top: -4rem;
  background: var(--white);
  margin-inline: 1.5rem;
  position: relative;

  @media ${device.desktop} {
    padding: 2.4rem;
    margin-inline: 4.6rem;

    .select-input-wrapper {
      padding-right: 3.2rem;
      padding-left: 1.6rem;
    }
  }

  .bdo-select-container {
    display: inline-block;

    @media ${device.deviceOnly} {
      width: 100%;
    }
  }

  .card-grid-wrapper {
    padding-inline: 0 !important;
  }
`;

export default compose(
  withStrictMode,
  withLocalization,
  withServerStyles
)(StyledEventWebinarLandingInsightList);
